<template>
  <div class="flex flex-column flex-grow-1" :class="`${AppConfig.theme}-theme`">
    <label class="label-top">{{ props.label }}</label>
    <div class="p-float-label">
      <InputNumber
        v-model="model"
        style="width: 100%" 
        class="number-input"
        :input-id="inputId"
        v-bind="{
          ...$attrs,
          mode: isDecimal ? 'decimal' : undefined,
          minFractionDigits: isDecimal ? 1 : 0,
          maxFractionDigits: isDecimal ? 2 : 0,
          allowEmpty: false
        }"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @blur="handleBlur"
      />
      <label class="label-border">{{ props.label }}</label>
    </div>
    <div class="flex">
      <small v-if="v$.value.$error && v$.value.$dirty" class="p-error">{{ props.label }} field has an error.</small>
      <small v-if="!v$.value.$error && props.required && !v$.value.$dirty" class="help-text">*{{ t('general.required') }}</small>
    </div>
  </div>
</template>
<script setup>
import { computed, reactive, ref } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, maxValue } from '@vuelidate/validators'
import { useI18nUtils } from '@/composables/utils/useI18nUtils.js'
import AppConfig from '~/config/appConfig.js'

const props = defineProps({
  label: String,
  required: Boolean,
  isDecimal: {
    type: Boolean,
    default: false
  },
  maxValue: {
    type: Number,
    required: false
  }
})

const model = defineModel()
const inputId = ref(`number-input-${Math.random().toString(36).substr(2, 9)}`)

const { t } = useI18nUtils()
const value = reactive(model)

const rules = computed(() => {
  const v = {}
  if (props.required) {
    if (props.maxValue != undefined) {
      v.value = { required, maxValue: maxValue(props.maxValue) }
    } else {
      v.value = { required }
    }
  } else {
    v.value = {}
  }

  return v
})

const v$ = useVuelidate(rules, { value }, { $lazy: true })

const inputMode = computed(() => (props.isDecimal ? 'decimal' : null))

const handleBlur = () => {
  v$.value.$touch()
}

// Prevent non-numeric input on keydown
const handleKeydown = (event) => {
  const key = event.key
  const keyCode = event.keyCode || event.which
  
  // Allow: backspace, delete, tab, escape, enter
  if ([8, 9, 27, 13, 46].includes(keyCode) ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      (event.ctrlKey && [65, 67, 86, 88, 90].includes(keyCode)) ||
      // Allow: home, end, left, right, up, down
      (keyCode >= 35 && keyCode <= 40)) {
    return true
  }
  
  // Allow numeric keys (0-9) from both main keyboard and numpad
  if ((keyCode >= 48 && keyCode <= 57) || (keyCode >= 96 && keyCode <= 105)) {
    return true
  }
  
  // Allow decimal point for decimal mode
  if (props.isDecimal && (key === '.' || key === ',' || keyCode === 190 || keyCode === 110)) {
    return true
  }
  
  // Allow minus sign at the beginning
  if (key === '-' || keyCode === 189 || keyCode === 109) {
    const inputElement = event.target
    const currentValue = inputElement?.value || ''
    const selectionStart = inputElement?.selectionStart || 0
    // Only allow minus at the beginning
    if (selectionStart === 0 && !currentValue.includes('-')) {
      return true
    }
  }
  
  // Prevent all other keys (letters, special characters, etc.)
  event.preventDefault()
  return false
}

// Prevent non-numeric paste
const handlePaste = (event) => {
  event.preventDefault()
  const pasteText = (event.clipboardData || window.clipboardData).getData('text')
  
  // Only allow numeric characters (and decimal point/minus for decimal mode)
  const regex = props.isDecimal ? /^-?\d*\.?\d*$/ : /^-?\d*$/
  
  if (regex.test(pasteText)) {
    // Find the actual input element within PrimeVue's InputNumber component
    setTimeout(() => {
      const inputElement = document.querySelector(`#${inputId.value} input`) || 
                          event.target?.querySelector('input')
      
      if (inputElement) {
        const start = inputElement.selectionStart || 0
        const end = inputElement.selectionEnd || 0
        const currentValue = inputElement.value || ''
        const newValue = currentValue.substring(0, start) + pasteText + currentValue.substring(end)
        
        // Validate the combined value
        if (regex.test(newValue)) {
          const numValue = props.isDecimal ? 
            (newValue ? parseFloat(newValue) : null) : 
            (newValue ? parseInt(newValue) : null)
          
          if (numValue !== null || newValue === '' || newValue === '-') {
            model.value = numValue
            // Restore cursor position
            setTimeout(() => {
              inputElement.setSelectionRange(start + pasteText.length, start + pasteText.length)
            }, 0)
          }
        }
      }
    }, 0)
  }
}
</script>

<style scoped>
:deep(.p-inputnumber-input) {
  height: 40px;
  width: 100%;
  box-shadow: 1px 2px 4px #00000029;
  border: 0.5px solid #E5E5E5;
  border-radius: 8px;
}

label {
  font-weight: 550;
  color: #013315;
}

.label-top {
  margin-bottom: 8px;
  display: none;
}

.text-input .description{
  display: none
}

@media screen and (min-width: 768px) {
  .label-top, .text-input .description {
    display: block;
  }

  .label-border {
    display: none;
  }
}

.text-input .description {
  margin-bottom: 8px;
  font-size: 0.875rem;
  color: #484649;
}

.sbg-theme {
  label {
    color: rgba(0, 0, 0, 0.6);
    font-weight: normal;
    width: auto;
  }

  .label-top {
    display: none;
  }

  .label-border {
    display: block;
  }

  .p-focus ~ label {
    color: #00afa5;
  }

  :deep(.p-inputnumber-input) {
    border-radius: 8px;
    height: auto;
    line-height: normal;
    box-shadow: none !important;
    border: 1px solid rgba(0, 0, 0, 0.38) !important;
  }
}

</style>
